/**
 * LookAtTargetAction - behavior tree action for orienting toward targets.
 */

import * as THREE from 'three';

import { AerialMovementComponent } from '../../../../../components/movement/AerialMovementComponent.js';
import { NpcMovementComponent } from '../../../../../components/movement/NpcMovementComponent.js';
import { EngineClass } from '../../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../../BehaviorNode.js';
import { BehaviorAction, type BehaviorActionOptions } from '../../BehaviorAction.js';

import type { Actor } from '../../../../../actors/Actor.js';
import type { Blackboard } from '../../../blackboard/Blackboard.js';

export interface LookAtTargetActionOptions extends BehaviorActionOptions {
  targetActorKey?: string;
  targetPositionKey?: string;
  angleThreshold?: number;
  rotationSpeed?: number;
  horizontalOnly?: boolean;
  speed?: number;
}

@EngineClass('LookAtTargetAction')
export class LookAtTargetAction extends BehaviorAction {
  private targetActorKey?: string;
  private targetPositionKey?: string;
  private angleThreshold: number;
  private rotationSpeed: number;
  private horizontalOnly: boolean;
  private speed: number;

  constructor(options: LookAtTargetActionOptions = {}) {
    super({
      name: 'LookAt',
      ...options
    });

    this.targetActorKey = options.targetActorKey ?? 'PlayerActor';
    this.targetPositionKey = options.targetPositionKey;
    this.angleThreshold = options.angleThreshold ?? Math.PI / 16; // ~11.25 degrees
    this.rotationSpeed = options.rotationSpeed ?? 3.0; // radians per second
    this.horizontalOnly = options.horizontalOnly ?? true;
    this.speed = options.speed ?? 1.0;
  }

  protected override onInitialize(blackboard: Blackboard): void {}

  protected override onEnter(blackboard: Blackboard): void {}

  protected override async onUpdate(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return BehaviorStatus.Failure;
    }

    // Get target position
    const targetPos = this.getTargetPosition(blackboard);
    if (!targetPos) {
      return BehaviorStatus.Failure;
    }

    // Try to get either type of movement component
    const npcMovementComponent = owner.getComponent(NpcMovementComponent);
    const aerialMovementComponent = owner.getComponent(AerialMovementComponent);
    const movementComponent = npcMovementComponent ?? aerialMovementComponent;

    if (!movementComponent) {
      return BehaviorStatus.Failure;
    }

    // Set the look-at target on the movement component (both have same API)
    movementComponent.setLookAtTarget(targetPos, this.angleThreshold);

    // Check if we're done looking at the target (both have same API)
    if (movementComponent.isLookingAtTargetComplete()) {
      return BehaviorStatus.Success;
    }

    return BehaviorStatus.Running;
  }

  protected override onExit(blackboard: Blackboard, status: BehaviorStatus): void {
    const owner = this.getOwner(blackboard);
    if (owner) {
      // Try to get either type of movement component
      const npcMovementComponent = owner.getComponent(NpcMovementComponent);
      const aerialMovementComponent = owner.getComponent(AerialMovementComponent);
      const movementComponent = npcMovementComponent ?? aerialMovementComponent;

      if (movementComponent) {
        // Clear the look-at target when exiting (both have same API)
        movementComponent.clearLookAtTarget();
      }
    }
  }

  private getTargetPosition(blackboard: Blackboard): THREE.Vector3 | null {
    if (this.targetActorKey) {
      const actor = blackboard.getValue<Actor>(this.targetActorKey);
      if (actor) {
        const position = new THREE.Vector3();
        actor.rootComponent.getWorldPosition(position);
        return position;
      }
    }

    if (this.targetPositionKey) {
      const position = blackboard.getValue<THREE.Vector3>(this.targetPositionKey);
      if (position) {
        return position.clone();
      }
    }

    return null;
  }
}
